import { trackPropertyInfoCsEvent } from 'app/client/analytics';
import { getEnvelope, getPropertyInfo } from 'app/client/analytics/utils';
import { store } from 'app/shared/flux/store/configureStore';
import type { ListingDetails } from 'app/types/listingDetails.type';

/**

Copy and paste to quickly build out event templates
Idea: Need to automate this somehow?
{
    envelope: {
        event_template_id: '',
        event_template_version_id: '',
        event_type_id: '',
        event_type_version_id: ''
    },
    clickstream_trigger: {
        trigger_location_nm: '',
        trigger_type_nm: '',
        trigger_object_nm: '',
        trigger_source_nm: ''
    },
    semantic: {
        semantic_event_nm: ''
    }
}

To find more information related to a specific event on data portal, use event_type_id:
//       https://dataportal.zillowgroup.net/event-registry/{event_type_id}
// e.g., https://dataportal.zillowgroup.net/event-registry/4785
*/

// Valid input for frequently used triggers
type BuildingPpcTriggerObject = 'home_details_component|property_details' | 'home_details_component|summary';

export type SaveHomeTriggerLocation =
  | 'user_item_list_page'
  | 'my_hub_page'
  | 'home_details'
  | 'hp_homepage'
  | 'user_item_viewed_page'
  | 'search_results'
  | 'user_item_hidden_page';
export type SaveHomeTriggerObject =
  | 'property_card'
  | 'my_hub_component|last_search'
  | 'home_details_component|recently_viewed'
  | 'home_details_component|more_from_provider'
  | 'home_details_component|post_contact_modal'
  | 'my_hub_component|contact'
  | 'my_hub_component|last_viewed'
  | 'my_hub_component|last_search'
  | 'home_details_component|action_bar'
  | 'my_hub_component|favorite';

// https://dataportal.zillowgroup.net/event-registry/4804
export const emitTrackSaveHomeClickEvent = ({
  listing,
  triggerLocation,
  triggerObject,
}: {
  listing: ListingDetails | null | undefined;
  triggerLocation: SaveHomeTriggerLocation;
  triggerObject: SaveHomeTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '4804', eventVersion: '2', templateId: '285', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'button_to_save_home',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'save_home',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5051
export const emitTrackUnSaveHomeClickEvent = ({
  listing,
  triggerLocation,
  triggerObject,
}: {
  listing: ListingDetails | null | undefined;
  triggerLocation: SaveHomeTriggerLocation;
  triggerObject: SaveHomeTriggerObject;
}) => {
  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5051', eventVersion: '2', templateId: '285', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'button_to_save_home',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'unsave_home',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5052
export const emitTrackHideHomeFromHdpEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5052', eventVersion: '2', templateId: '285', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_hide_home',
      trigger_object_nm: 'home_details_component|action_bar',
    },
    semantic: {
      semantic_event_nm: 'hide_home',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5055
export const emitTrackUnhideHomeFromHdpEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5055', eventVersion: '2', templateId: '285', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_hide_home',
      trigger_object_nm: 'home_details_component|action_bar',
    },
    semantic: {
      semantic_event_nm: 'unhide_home',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// Note (for Clickstream testing): No GA event associated with this.
// This should be attached to both Upper and Lower PPC events.
// https://dataportal.zillowgroup.net/event-registry/5056
export const emitTrackBuildingPayPerLinkClickEvent = ({
  triggerObject,
}: {
  triggerObject: BuildingPpcTriggerObject;
}) => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5056', eventVersion: '2', templateId: '285', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_open_pay_per_click',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'building_pay_per_link',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5566
export const emitTrackLowerPpcClickEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5566',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_open_pay_per_click',
      trigger_object_nm: 'home_details_component|property_details',
    },
    semantic: {
      semantic_event_nm: 'ppc_click_lower',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5567
export const emitTrackUpperPpcClickEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5567',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_open_pay_per_click',
      trigger_object_nm: 'home_details_component|summary',
    },
    semantic: {
      semantic_event_nm: 'ppc_click_upper',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5572
export const emitTrackApplyNowClickEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5572',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_open_apply_now_interstitial',
      trigger_object_nm: 'home_details_component|summary',
    },
    semantic: {
      semantic_event_nm: 'apply_now_click_hdp',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5573
export const emitTrackRentalAppPreStartEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5573',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_open_apply_now_form',
      trigger_object_nm: 'home_details_component|application_interstitial',
    },
    semantic: {
      semantic_event_nm: 'rental_application_pre_start',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5715
export const emitTrackReportHomeFromHdpEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5715', eventVersion: '1', templateId: '4', templateVersion: '1' }),

    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_report_home',
      trigger_object_nm: 'home_details_component|action_bar',
    },
    semantic: {
      semantic_event_nm: 'report_home',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5716
export const emitTrackHdpNextClickEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5716', eventVersion: '1', templateId: '4', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_next_hdp',
      trigger_object_nm: 'home_details_component|action_bar',
    },
    semantic: {
      semantic_event_nm: 'next_hdp',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5901
export const emitTrackHdpSimilarListingClickEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5901', eventVersion: '1', templateId: '4', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'property_card',
      trigger_object_nm: 'home_details_component|similar_listings',
    },
    semantic: {
      semantic_event_nm: 'similar_listing_click',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5993
export const emitTrackHdpPhotoGalleryOpenEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5993', eventVersion: '1', templateId: '4', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_open_gallery',
      trigger_object_nm: 'home_details_component|photos',
    },
    semantic: {
      semantic_event_nm: 'open_photo_player',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5717
export const emitTrackHdpPrevClickEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5717', eventVersion: '1', templateId: '4', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_prev_hdp',
      trigger_object_nm: 'home_details_component|action_bar',
    },
    semantic: {
      semantic_event_nm: 'prev_hdp',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5718
export const emitTrackShareHomeFromHdpEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({ eventId: '5718', eventVersion: '1', templateId: '4', templateVersion: '1' }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'button_to_share_home',
      trigger_object_nm: 'home_details_component|action_bar',
    },
    semantic: {
      semantic_event_nm: 'share_home',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5925
export const emitTrackHdpNativeShareEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5925',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'native_share_tray',
      trigger_object_nm: 'home_details_component|share_options',
    },
    semantic: {
      semantic_event_nm: 'native_share',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5926
export const emitTrackHdpCopyShareUrlEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5926',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_copy_share_url',
      trigger_object_nm: 'home_details_component|share_options',
    },
    semantic: {
      semantic_event_nm: 'copy_url_share',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5927
export const emitTrackHdpTextShareEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5927',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_text_share_url',
      trigger_object_nm: 'home_details_component|share_options',
    },
    semantic: {
      semantic_event_nm: 'text_share',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/5928
export const emitTrackHdpEmailShareEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '5928',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_email_share_url',
      trigger_object_nm: 'home_details_component|share_options',
    },
    semantic: {
      semantic_event_nm: 'email_share',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};

// https://dataportal.zillowgroup.net/event-registry/7679
export const emitTrackCostsAndFeesModalOpenEvent = () => {
  const state = store.getState();
  const listing = state.currentListingDetails.currentListing;

  trackPropertyInfoCsEvent({
    envelope: getEnvelope({
      eventId: '7679',
      eventVersion: '1',
      templateId: '4',
      templateVersion: '1',
    }),
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'home_details',
      trigger_source_nm: 'link_to_open_cost_and_fees',
      trigger_object_nm: 'home_details_component|summary',
    },
    semantic: {
      semantic_event_nm: 'cost_and_fees_cta_click',
      topic_tag_txt: [],
    },
    ...getPropertyInfo(listing),
  });
};
